import React, { useEffect, useState, useMemo, useCallback, memo } from "react";
import {
  Button,
  Input,
  DatePicker,
  Select,
  Card,
  Spin,
  Pagination,
  message,
  Badge,
  Modal,
  Form,
  Input as AntInput,
  Tooltip,
  Tag,
  Space,
  Popconfirm,
  Checkbox,
  Tabs,
  Drawer,
  Image,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  CalendarOutlined,
  FilterOutlined,
  MenuOutlined,
  CheckOutlined,
  LockOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  CloseCircleOutlined,
  CheckSquareOutlined,
  EditOutlined,
  PlusOutlined,
  InfoCircleOutlined,
  UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import PageTitle from "../../Ui/PageTitle";
import { TbCancel } from "react-icons/tb";
import {
  FaCheck,
  FaUser,
  FaClock,
  FaMapMarkerAlt,
  FaUserPlus,
  FaEdit,
} from "react-icons/fa";
import { IoPersonAddOutline, IoReload } from "react-icons/io5";
import { MdOutlineArticle } from "react-icons/md";
import ReportModal from "./ReportModal";
import { useClinics } from "../../../hooks/useClinics";
import { useBookings } from "../../../hooks/useBookings";
import { useQueryClient } from "@tanstack/react-query";
import { FaCalendar, FaLock, FaPaperclip, FaUserDoctor } from "react-icons/fa6";
import drEasyApi from "../../../Api/Customapi";
import { getImg } from "../../../Api/getImg";
import { Link, useNavigate } from "react-router-dom";
import { CgCheck } from "react-icons/cg";
import { BiBlock, BiCalendarEvent } from "react-icons/bi";
import PatientAssignmentModal from "../../Modals/SelectPatientModal";
import { SiActivision } from "react-icons/si";
import CheckableTag from "antd/es/tag/CheckableTag";
import InvoiceModal from "./InvoiceModal";
import SpecialistAssignmentModal from "./SpecialistAssignModal";
import { GiPaper } from "react-icons/gi";
import { IoMdPaper } from "react-icons/io";
import ReportViewModal from "./ReportViewModal";
import PatientInfoModal from "./PatientInfoModal";
import CreatePatientModal from "../PatientsList/CreatePatientModal";
import { BsCalendarPlus } from "react-icons/bs";
import CreateBookingModal from "./CreateBookingModal";
import ConsultantAssignmentModal from "../PatientsList/ConsultantAssignmentModal";
import AssignBookingToConsultantModal from "./AssignBookingToConsultantModal";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = AntInput;
const { TabPane } = Tabs;
const statusEnum = {
  Pending: 0,
  Cancelled: 1,
  Completed: 2,
  Skiped: 3,
  Active: 4,
};
    const role = localStorage.getItem("role");

// Status configuration
const statusConfig = {
  Pending: {
    color: "bg-gradient-to-r from-amber-400 to-orange-500",
    textColor: "text-yellow-400",
    icon: <ClockCircleOutlined />,
    bgLight: "bg-amber-50",
    borderColor: "border-amber-200",
  },
  Cancelled: {
    color: "bg-gradient-to-r from-red-400 to-red-600",
    textColor: "text-red-400",
    icon: <BiBlock className="inline mb-[1px]" />,
    bgLight: "bg-red-50",
    borderColor: "border-red-200",
  },
  Completed: {
    color: "bg-gradient-to-r from-primay/90 to-primary",
    icon: <CheckOutlined />,
    textColor: "text-green-400",
    bgLight: "bg-green-50",
    borderColor: "border-green-200",
  },
  Skiped: {
    color: "bg-gradient-to-r from-gray-400 to-gray-500",
    textColor: "text-gray-500",
    icon: <ReloadOutlined />,
    bgLight: "bg-gray-50",
    borderColor: "border-gray-200",
  },
  Active: {
    color: "bg-gradient-to-r from-green-400 to-green-500",
    textColor: "text-gray-500",
    icon: <ReloadOutlined />,
    bgLight: "bg-gray-50",
    borderColor: "border-gray-200",
  },
};

const useResponsive = () => {
  const [breakpoint, setBreakpoint] = useState("lg");
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const updateBreakpoint = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setBreakpoint("sm");
        setIsMobile(true);
        setIsTablet(false);
      } else if (width < 768) {
        setBreakpoint("md");
        setIsMobile(false);
        setIsTablet(true);
      } else if (width < 1024) {
        setBreakpoint("lg");
        setIsMobile(false);
        setIsTablet(true);
      } else if (width < 1280) {
        setBreakpoint("xl");
        setIsMobile(false);
        setIsTablet(false);
      } else {
        setBreakpoint("2xl");
        setIsMobile(false);
        setIsTablet(false);
      }
    };

    updateBreakpoint();
    window.addEventListener("resize", updateBreakpoint);
    return () => window.removeEventListener("resize", updateBreakpoint);
  }, []);

  return { breakpoint, isMobile, isTablet };
};

const BookingCard = memo(
  ({
    booking,
    index,
    onComplete,
    onCancel,
    onGuestAssignment,
    onInvoiceClick,
    onMakeActive,
    updateStatusMutation,
    onAssignToSpecialist,
    isMobile,
    onViewReport,
    onPatientClick,
    onAssignToConsultant,
    setAssignConsultantBooking,
    setAssignConsultantVisible,
    setPatientInfoModalVisible,
    setCurrentPatientId,
    setUpdateOnly
  }) => {
    const status = statusConfig[booking.status] || statusConfig.Pending;
    const navigate = useNavigate();
    const [expanded, setExpanded] = useState(false);
    const isLong = booking.notes.split('\n').length > 1 || booking.notes.length > 80;
    const handleCompleteClick = useCallback(
      () => onComplete(booking),
      [booking, onComplete],
    );

    const handleCancelClick = useCallback(
      () => onCancel(booking),
      [booking, onCancel],
    );
    const handleAssignToConsultantClick = useCallback(() => {
      onAssignToConsultant(booking);
    }, [booking, onAssignToConsultant]);

    const handleSkipClick = useCallback(() => {
      updateStatusMutation.mutate({
        bookingId: booking.id,
        newStatus: "Skiped",
      });
    }, [booking.id, updateStatusMutation]);

    const handleInvoiceButtonClick = useCallback(() => {
      onInvoiceClick(booking);
    }, [booking, onInvoiceClick]);

    const handleActiveButtonClick = useCallback(() => {
      onMakeActive(booking);
      setCurrentPatientId(booking.patientId)
    }, [booking, onMakeActive]);

    const handlePatientNameClick = useCallback(
      (e) => {
        e.stopPropagation();
        if (!booking.isGuest && booking.profileId) {
          navigate(`/patients/${booking.profileId}`, {
            state: {
              patient: {
                id: booking.profileId,
                fullName: booking.patientName,
                accountId: booking.patientId,
                imageId: booking.patientProfile,
                email: booking.patientEmail || "",
              },
            },
          });
        }
      },
      [booking, navigate],
    );
    const handleEdit = useCallback((e) => {
      e.stopPropagation();
      setUpdateOnly(true)
      setCurrentPatientId(booking.patientId)
      setPatientInfoModalVisible(true);
    }, [booking.patientId]);
    console.log(booking.patientId, "sd");

    return (
      <div
        className={`group relative bg-white rounded-xl sm:rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border ${status.borderColor
          } overflow-hidden ${isMobile ? "mb-4" : ""}`}
        style={{
          animationDelay: `${index * 100}ms`,
          animation: "fadeInUp 0.6s ease-out forwards",
        }}
      >
        <div
          className={`absolute top-2 right-2 sm:top-4 sm:right-4 px-2 py-1 sm:px-3 sm:py-1 rounded-full text-xs font-bold ${status.bgLight
            } border ${status.borderColor} ${status.textColor
            } shadow-lg transform ${isMobile ? "rotate-0" : "rotate-3 group-hover:rotate-0"
            } transition-transform duration-300`}
        >
          {status.icon} {booking.status}
        </div>

        {booking.isGuest && (
          <div
            className={`absolute top-3 right-32 sm:top-4 sm:right-32 px-2 py-[.5px] sm:px-3 rounded-full text-xs font-bold border bg-red-100 border-red-300 text-red-400 shadow-lg transform ${isMobile ? "rotate-0" : "rotate-3 group-hover:rotate-0"
              } transition-transform duration-300`}
          >
            Guest
          </div>
        )}

        <div className="p-4 sm:p-6">
          <div
            className={`flex items-center mb-3 sm:mb-4 ${isMobile ? "flex-wrap" : ""
              }`}
          >
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-primary to-primary_hover rounded-full flex items-center justify-center text-white font-bold text-base sm:text-lg shadow-lg flex-shrink-0">
              {booking.patientProfile ? (
                <img
                  src={getImg(booking.patientProfile)}
                  className="rounded-full border-primary w-full h-full object-cover"
                  fallback="https://res.cloudinary.com/dk2uh7sho/image/upload/v1750278754/default-img_tbc2pe.jpg"
                />
              ) : (
                <span>{booking.patientName.charAt(0).toUpperCase()}</span>
              )}
            </div>
            <div className="ml-3 sm:ml-4 min-w-0 flex-1">
              <div className="flex items-center  gap-2">

                <button
                  disabled={booking.isGuest}
                  onClick={handlePatientNameClick}
                  className={`font-bold text-gray-800 group-hover:text-blue-600 transition-colors duration-300 ${isMobile ? "text-base truncate" : "text-lg"
                    } ${!booking.isGuest ? "hover:underline cursor-pointer" : "cursor-not-allowed opacity-60"} bg-transparent border-none p-0 text-left  truncate`}
                >
                  {booking.patientName}
                </button>
                {role == 4 &&
                  <Tooltip title="Edit patient">

                    <Button
                      title='Edit'
                      size='small'
                      icon={<FaEdit />}
                      onClick={handleEdit}

                    />
                  </Tooltip>}
              </div>
              <div className="flex items-center text-xs sm:text-sm text-gray-500">
                <FaMapMarkerAlt className="mr-1 flex-shrink-0" />
                <span className={isMobile ? "truncate" : ""}>
                  {booking.clinicName}
                </span>
              </div>
            </div>
          </div>

          {/* Date & Time */}
          <div
            className={`p-3 sm:p-4 rounded-lg sm:rounded-xl mb-3 sm:mb-4 ${status.bgLight} border ${status.borderColor}`}
          >
            <div
              className={`${isMobile ? "space-y-2" : "flex items-center justify-between"
                }`}
            >
              <div>
                <div className="text-xs sm:text-sm font-semibold text-gray-700">
                  <CalendarOutlined className="me-1"/>
                  {dayjs(booking.bookingDate).format(
                    isMobile ? "MMM D" : "MMM D, YYYY",
                  )}
                </div>
                <div className="flex items-center text-xs sm:text-sm text-gray-600 mt-1">
                  <FaClock className="mr-2 flex-shrink-0" />
                  <span className={isMobile ? "text-xs" : ""}>
                    {dayjs(booking.startTime, "HH:mm").format("h:mm A")} -{" "}
                    {dayjs(booking.endTime, "HH:mm").format("h:mm A")}
                  </span>
                </div>
                <div className="flex items-center text-xs sm:text-sm text-gray-600 mt-1">
                  <FaUserDoctor className="mr-2 flex-shrink-0" />
                  <span className={isMobile ? "text-xs" : ""}>
                    {booking?.doctorName
                      ? `Dr. ${booking.doctorName}`
                      : "Doctor consultation"}
                  </span>
                </div>
                {(() => {


                  return (
                    <div className="flex items-start text-xs sm:text-sm text-gray-600 mt-1">
                      <InfoCircleOutlined className="mr-2 flex-shrink-0 mt-0.5" />
                      <span className={isMobile ? "text-xs" : ""}>
                        <span
                          style={{
                            display: '-webkit-box',
                            WebkitLineClamp: expanded ? 'unset' : 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: expanded ? 'visible' : 'hidden',
                          }}
                        >
                          {booking.notes ? booking.notes : "N/A"}
                        </span>
                        {isLong && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpanded(prev => !prev);
                            }}
                            className="text-blue-500 hover:text-blue-700 font-medium ml-1 bg-transparent border-none p-0 cursor-pointer"
                          >
                            {expanded ? 'See less' : 'See more'}
                          </button>
                        )}
                      </span>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {role == 4 && (
            <div
              className={`flex items-center justify-between pt-3 sm:pt-4 border-t border-gray-100 ${isMobile ? "flex-wrap gap-2" : ""
                }`}
            >
              <div
                className={`flex ${isMobile ? "gap-1" : "space-x-2"} ${isMobile ? "flex-wrap" : ""
                  }`}
              >
                {!booking.isGuest &&
                  booking.status !== "Skiped" &&
                  booking.status !== "Cancelled" && (
                    <>
                      {booking.status !== "Completed" && (
                        <Tooltip title="Assign Book To Consultant">
                          <Button
                            type=""
                            icon={<FaUserDoctor />}
                            onClick={() => {
                              setAssignConsultantBooking(booking);
                              setAssignConsultantVisible(true);
                            }}
                            size={isMobile ? "small" : "middle"}
                            className="bg-secondary -500 border-none rounded-full flex items-center justify-center shadow-lg hover:bg-secondary_hover text-white hover:shadow-xl transform hover:scale-110 transition-all duration-300"
                          />
                        </Tooltip>)}
                      <Tooltip title="Assign To Specialist">
                        <Button
                          type=""
                          icon={<FaUserPlus />}
                          onClick={() => onAssignToSpecialist(booking)}
                          size={isMobile ? "small" : "middle"}
                          className="bg-primary border-none rounded-full flex items-center justify-center shadow-lg hover:bg-primary_hover text-white hover:shadow-xl transform hover:scale-110 transition-all duration-300"
                        />
                      </Tooltip>

                    </>
                  )}
                {booking.status == "Cancelled" && <div className="flex items-center text-xs sm:text-sm text-gray-600 mt-1">
                  <UserOutlined className="mr-2 flex-shrink-0" />
                  <span className={isMobile ? "text-xs" : ""}>
                    {booking?.cancelledBy
                      ? `Cancelled by ${booking.cancelledBy}`
                      : "No cancelation info provied"}
                  </span>
                </div>}
                {/* Assign Guest to User */}
                {booking.isGuest && (
                  <Tooltip title="Assign to user">
                    <Button
                      type=""
                      icon={<IoPersonAddOutline className="text-lg" />}
                      onClick={() => onGuestAssignment(booking)}
                      size={isMobile ? "small" : "middle"}
                      className="bg-red-400 to-red-600 border-none text-white rounded-full hover:bg-red-500 flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-300"
                    />
                  </Tooltip>
                )}

                {/* Pending — no invoice */}
                {booking.status === "Pending" &&
                  !booking.invoiceId &&
                  !booking.isGuest && (
                    <>
                      <Tooltip title="Make Invoice">
                        <Button
                          type=""
                          icon={<FaPaperclip />}
                          onClick={handleInvoiceButtonClick}
                          size={isMobile ? "small" : "middle"}
                          className="bg-secondary border-none rounded-full flex items-center justify-center shadow-lg hover:bg-green-600 text-white hover:shadow-xl transform hover:scale-110 transition-all duration-300"
                        />
                      </Tooltip>

                      <Tooltip title="Mark as Skipped">
                        <Button
                          type=""
                          icon={<IoReload />}
                          onClick={handleSkipClick}
                          size={isMobile ? "small" : "middle"}
                          className="bg-gray-400 to-gray-500 border-none text-white rounded-full flex items-center justify-center shadow-lg hover:bg-yellow-400 hover:shadow-xl transform hover:scale-110 transition-all duration-300"
                        />
                      </Tooltip>

                      <Popconfirm
                        title="Are you sure you want to cancel this booking?"
                        onConfirm={handleCancelClick}
                        okText="Yes"
                        cancelText="No"
                        placement={isMobile ? "top" : "bottom"}
                      >
                        <Tooltip title="Cancel Booking">
                          <Button
                            type=""
                            icon={<TbCancel />}
                            size={isMobile ? "small" : "middle"}
                            className="bg-red-400 to-red-600 border-none text-white rounded-full hover:bg-red-500 flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-300"
                          />
                        </Tooltip>
                      </Popconfirm>
                    </>
                  )}

                {/* Pending — with invoice */}
                {booking.status === "Pending" &&
                  booking.invoiceId &&
                  !booking.isGuest && (
                    <>
                      <Tooltip title="Edit Invoice">
                        <Button
                          type=""
                          icon={<EditOutlined />}
                          onClick={handleInvoiceButtonClick}
                          size={isMobile ? "small" : "middle"}
                          className="bg-primary border-none rounded-full flex items-center justify-center shadow-lg hover:bg-green-600 text-white hover:shadow-xl transform hover:scale-110 transition-all duration-300"
                        />
                      </Tooltip>
                      {/* {new Date(booking.bookingDate).setHours(0, 0, 0, 0) <=
                        new Date().setHours(0, 0, 0, 0) && ( */}
                      <Tooltip title="Make Active">
                        <Button
                          type=""
                          icon={<CheckSquareOutlined />}
                          onClick={handleActiveButtonClick}
                          size={isMobile ? "small" : "middle"}
                          className="bg-emerald-500 border-none rounded-full flex items-center justify-center shadow-lg hover:bg-green-600 text-white hover:shadow-xl transform hover:scale-110 transition-all duration-300"
                        />
                      </Tooltip>
                      {/* )} */}
                    </>
                  )}

                {/* Active */}
                {booking.status === "Active" && !booking.isGuest && (
                  <>
                    <Tooltip title="Edit Invoice">
                      <Button
                        type=""
                        icon={<EditOutlined />}
                        onClick={handleInvoiceButtonClick}
                        size={isMobile ? "small" : "middle"}
                        className="bg-primary border-none rounded-full flex items-center justify-center shadow-lg hover:bg-green-600 text-white hover:shadow-xl transform hover:scale-110 transition-all duration-300"
                      />
                    </Tooltip>
                    {new Date(booking.bookingDate).setHours(0, 0, 0, 0) <=
                      new Date().setHours(0, 0, 0, 0) && (
                        <Tooltip title="Mark as Completed">
                          <Button
                            type=""
                            icon={<FaCheck />}
                            onClick={handleCompleteClick}
                            size={isMobile ? "small" : "middle"}
                            className="bg-emerald-500 border-none rounded-full flex items-center justify-center shadow-lg hover:bg-green-600 text-white hover:shadow-xl transform hover:scale-110 transition-all duration-300"
                          />
                        </Tooltip>
                      )}
                  </>
                )}

                {/* Completed */}
                {booking.status === "Completed" && !booking.isGuest && (
                  <>
                    <Tooltip title="Edit Invoice">
                      <Button
                        type=""
                        icon={<EditOutlined />}
                        onClick={handleInvoiceButtonClick}
                        size={isMobile ? "small" : "middle"}
                        className="bg-primary border-none rounded-full flex items-center justify-center shadow-lg hover:bg-green-600 text-white hover:shadow-xl transform hover:scale-110 transition-all duration-300"
                      />
                    </Tooltip>
                    <Tooltip title="View Report">
                      <Button
                        type=""
                        icon={<IoMdPaper />}
                        onClick={() => onViewReport(booking)}
                        size={isMobile ? "small" : "middle"}
                        className="bg-primary border-none rounded-full flex items-center justify-center shadow-lg hover:bg-primary_hover text-white hover:shadow-xl transform hover:scale-110 transition-all duration-300"
                      />
                    </Tooltip>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-purple-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-xl sm:rounded-2xl"></div>
      </div>
    );
  },
);

const BookingsScreen = () => {
  const { breakpoint, isMobile, isTablet } = useResponsive();
  const [reportModalVisible, setReportModalVisible] = useState(false);
  const [currentReportBooking, setCurrentReportBooking] = useState(null);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [currentBooking, setCurrentBooking] = useState(null);
  const [currentCompleteBooking, setCurrentCompleteBooking] = useState(null);
  const [doctorServices, setDoctorServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("today");
  const [filtersDrawerVisible, setFiltersDrawerVisible] = useState(false);
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [servicesForm] = Form.useForm();
  const [patientAssignmentModalVisible, setPatientAssignmentModalVisible] =
    useState(false);
  const [currentGuestBooking, setCurrentGuestBooking] = useState(null);
  const [invoiceModalVisible, setInvoiceModalVisible] = useState(false);
  const [currentInvoiceBooking, setCurrentInvoiceBooking] = useState(null);
  const [
    specialistAssignmentModalVisible,
    setSpecialistAssignmentModalVisible,
  ] = useState(false);
  const [currentStatus, setCurentStatus] = useState(null);
  const [consultantAssignModalVisible, setConsultantAssignModalVisible] =
    useState(false);
  const [currentConsultantBooking, setCurrentConsultantBooking] =
    useState(null);
  const [assignConsultantVisible, setAssignConsultantVisible] = useState(false);
  const [assignConsultantBooking, setAssignConsultantBooking] = useState(null);

  const [currentAssignmentBooking, setCurrentAssignmentBooking] =
    useState(null);
  const [currentPatientId, setCurrentPatientId] = useState(null)
  const [reportViewModalVisible, setReportViewModalVisible] = useState(false);
  const [currentViewReportBooking, setCurrentViewReportBooking] =
    useState(null);
  const [patientInfoModalVisible, setPatientInfoModalVisible] = useState(false);
  const [currentActivateBooking, setCurrentActivateBooking] = useState(null);
  const [createPatientModalVisible, setCreatePatientModalVisible] =
    useState(false);
  const [updateOnly, setUpdateOnly] = useState(false)
  const [createBookModal, setCreateBookModal] = useState(false);
  const handleGuestAssignmentClick = useCallback((booking) => {
    setCurrentGuestBooking(booking);
    setPatientAssignmentModalVisible(true);
  }, []);

  const handlePatientAssignmentModalClose = useCallback(() => {
    setPatientAssignmentModalVisible(false);
    setCurrentGuestBooking(null);
  }, []);

  const getPageSize = useMemo(() => {
    if (isMobile) return 4;
    if (isTablet) return 6;
    return 12;
  }, [isMobile, isTablet]);

  const getGridCols = useMemo(() => {
    if (isMobile) return 1;
    if (isTablet) return 2;
    if (breakpoint === "xl") return 3;
    return 4;
  }, [isMobile, isTablet, breakpoint]);

  // Separate pagination for each tab with responsive page sizes
  const [todayPagination, setTodayPagination] = useState({
    current: 1,
    pageSize: getPageSize,
    total: 0,
  });

  const [tomorrowPagination, setTomorrowPagination] = useState({
    current: 1,
    pageSize: getPageSize,
    total: 0,
  });

  const [yesterdayPagination, setYesterdayPagination] = useState({
    current: 1,
    pageSize: getPageSize,
    total: 0,
  });

  const [allPagination, setAllPagination] = useState({
    current: 1,
    pageSize: getPageSize,
    total: 0,
  });

  useEffect(() => {
    const newPageSize = getPageSize;
    setTodayPagination((prev) => ({ ...prev, pageSize: newPageSize }));
    setTomorrowPagination((prev) => ({ ...prev, pageSize: newPageSize }));
    setYesterdayPagination((prev) => ({ ...prev, pageSize: newPageSize }));
    setAllPagination((prev) => ({ ...prev, pageSize: newPageSize }));
  }, [getPageSize]);

  const [todayFilters, setTodayFilters] = useState({
    patientName: "",
    clinicId: undefined,
    fromDate: dayjs().startOf("day"),
    toDate: dayjs().endOf("day"),
  });

  const [tomorrowFilters, setTomorrowFilters] = useState({
    patientName: "",
    clinicId: undefined,
    fromDate: dayjs().add(1, "day").startOf("day"),
    toDate: dayjs().add(1, "day").endOf("day"),
  });

  const [yesterdayFilters, setYesterdayFilters] = useState({
    patientName: "",
    clinicId: undefined,
    fromDate: undefined,
    toDate: dayjs().subtract(1, "day").endOf("day"),
  });

  const [allFilters, setAllFilters] = useState({
    status: undefined,
    patientName: "",
    clinicId: undefined,
    fromDate: undefined,
    toDate: undefined,
  });

  const [isPrivate, setIsPRivate] = useState(false);

  const { clinicsQuery } = useClinics({
    pageNumber: 1,
    pageSize: 100,
    staleTime: Infinity,
  });

  const { bookingsQuery: todayBookingsQuery, updateStatusMutation } =
    useBookings(todayPagination, todayFilters);
  const { bookingsQuery: tomorrowBookingsQuery } = useBookings(
    tomorrowPagination,
    tomorrowFilters,
  );
  const { bookingsQuery: yesterdayBookingsQuery } = useBookings(
    yesterdayPagination,
    yesterdayFilters,
  );
  const { bookingsQuery: allBookingsQuery } = useBookings(
    allPagination,
    allFilters,
  );
  console.log(todayBookingsQuery.data, "Aaaa");

  const getCurrentTabData = useMemo(() => {
    switch (activeTab) {
      case "today":
        return {
          query: todayBookingsQuery,
          pagination: todayPagination,
          setPagination: setTodayPagination,
        };
      case "tomorrow":
        return {
          query: tomorrowBookingsQuery,
          pagination: tomorrowPagination,
          setPagination: setTomorrowPagination,
        };
      case "yesterday":
        return {
          query: yesterdayBookingsQuery,
          pagination: yesterdayPagination,
          setPagination: setYesterdayPagination,
        };
      case "all":
        return {
          query: allBookingsQuery,
          pagination: allPagination,
          setPagination: setAllPagination,
        };
      default:
        return {
          query: todayBookingsQuery,
          pagination: todayPagination,
          setPagination: setTodayPagination,
        };
    }
  }, [
    activeTab,
    todayBookingsQuery,
    tomorrowBookingsQuery,
    yesterdayBookingsQuery,
    allBookingsQuery,
    todayPagination,
    tomorrowPagination,
    yesterdayPagination,
    allPagination,
  ]);

  const getCurrentFilters = useMemo(() => {
    switch (activeTab) {
      case "today":
        return todayFilters;
      case "tomorrow":
        return tomorrowFilters;
      case "yesterday":
        return yesterdayFilters;
      case "all":
        return allFilters;
      default:
        return todayFilters;
    }
  }, [activeTab, todayFilters, tomorrowFilters, yesterdayFilters, allFilters]);

  const setCurrentFilters = useCallback(
    (newFilters) => {
      switch (activeTab) {
        case "today":
          setTodayFilters(newFilters);
          break;
        case "tomorrow":
          setTomorrowFilters(newFilters);
          break;
        case "yesterday":
          setYesterdayFilters(newFilters);
          break;
        case "all":
          setAllFilters(newFilters);
          break;
        default:
          setTodayFilters(newFilters);
      }
    },
    [activeTab],
  );
  const handleAssignToSpecialistClick = useCallback((booking) => {
    setCurrentAssignmentBooking(booking);
    setSpecialistAssignmentModalVisible(true);
  }, []);

  const handleSpecialistAssignment = useCallback(
    async (selectedSpecialist) => {
      try {
        const { query } = getCurrentTabData;
        await query.refetch();
        setSpecialistAssignmentModalVisible(false);
        setCurrentAssignmentBooking(null);
        message.success(
          `Booking assigned to ${selectedSpecialist.name} successfully!`,
        );
      } catch (error) {
        console.error("Error handling specialist assignment:", error);
      }
    },
    [getCurrentTabData],
  );

  const handleSpecialistModalClose = useCallback(() => {
    setSpecialistAssignmentModalVisible(false);
    setCurrentAssignmentBooking(null);
  }, []);
  const handleAssignToConsultantClick = useCallback((booking) => {
    setCurrentConsultantBooking(booking);
    setConsultantAssignModalVisible(true);
  }, []);

  const handleConsultantAssigned = useCallback(
    async (selectedConsultant) => {
      const { query } = getCurrentTabData;
      await query.refetch();
      setConsultantAssignModalVisible(false);
      setCurrentConsultantBooking(null);
      message.success(
        `Booking assigned to Dr. ${selectedConsultant.name} successfully!`,
      );
    },
    [getCurrentTabData],
  );

  const handleConsultantAssignModalClose = useCallback(() => {
    setConsultantAssignModalVisible(false);
    setCurrentConsultantBooking(null);
  }, []);

  const fetchDoctorServices = useCallback(async () => {
    setServicesLoading(true);
    try {
      const response = await drEasyApi.get("/api/Services/GetMyServices");
      setDoctorServices(response.data);
    } catch {
      // console.error("Error fetching services:", error);
      // message.error("Error fetching services");
    } finally {
      setServicesLoading(false);
    }
  }, []);

  const handleCompleteClick = useCallback(
    async (booking) => {
      if (!booking?.id) {
        message.warning('No booking selected to complete');
        return;
      }
      try {
        await updateStatusMutation.mutateAsync({
          bookingId: booking.id,
          newStatus: "Completed",
          serviceIds: null,
        });

        setCurrentViewReportBooking(booking);
        setReportViewModalVisible(true);

        message.success("Booking completed successfully!");
      } catch (error) {
        console.error("Error completing booking:", error);
      }
    },
    [updateStatusMutation],
  );

  const handleCancelClick = useCallback((booking) => {
    setCurrentBooking(booking);
    setCancelModalVisible(true);
  }, []);

  const handleEditInvoiceFromReport = useCallback(async () => {
    setReportViewModalVisible(false);
    if(role !=7){

      await fetchDoctorServices(); }
    setCurrentInvoiceBooking(currentViewReportBooking);
    setInvoiceModalVisible(true);
  }, [currentViewReportBooking, fetchDoctorServices]);

  const handleAssignSpecialistFromReport = useCallback(() => {
    setReportViewModalVisible(false);
    setCurrentAssignmentBooking(currentViewReportBooking);
    setSpecialistAssignmentModalVisible(true);
  }, [currentViewReportBooking]);

  const handleCancelConfirm = useCallback(() => {
    form
      .validateFields()
      .then((values) => {
        updateStatusMutation.mutate({
          bookingId: currentBooking.id,
          newStatus: "Cancelled",
          reason: values.reason,
        });
        handleCancelModalClose();
        form.resetFields();
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  }, [updateStatusMutation, currentBooking, form]);

  const handleServiceChange = useCallback((serviceId, checked) => {
    if (checked) {
      setSelectedServices((prev) => [...prev, serviceId]);
    } else {
      setSelectedServices((prev) => prev.filter((id) => id !== serviceId));
    }
  }, []);

  const handleCancelModalClose = useCallback(() => {
    setCancelModalVisible(false);
    form.resetFields();
  }, [form]);

  const handleTableChange = useCallback(
    (pagination) => {
      const { setPagination } = getCurrentTabData;
      setPagination(pagination);
    },
    [getCurrentTabData],
  );

  const handleSearch = useCallback(
    (value) => {
      const currentFilters = getCurrentFilters;
      setCurrentFilters({ ...currentFilters, patientName: value });
      const { setPagination } = getCurrentTabData;
      setPagination((prev) => ({ ...prev, current: 1 }));
    },
    [getCurrentFilters, setCurrentFilters, getCurrentTabData],
  );

  const handleStatusChange = useCallback(
    (value) => {
      const currentFilters = getCurrentFilters;
      setCurrentFilters({ ...currentFilters, status: value });
      const { setPagination } = getCurrentTabData;
      setPagination((prev) => ({ ...prev, current: 1 }));
    },
    [getCurrentFilters, setCurrentFilters, getCurrentTabData],
  );

  const handleClinicChange = useCallback(
    (value) => {
      const currentFilters = getCurrentFilters;
      setCurrentFilters({ ...currentFilters, clinicId: value });
      const { setPagination } = getCurrentTabData;
      setPagination((prev) => ({ ...prev, current: 1 }));
    },
    [getCurrentFilters, setCurrentFilters, getCurrentTabData],
  );

  const handleDateChange = useCallback(
    (dates) => {
      const currentFilters = getCurrentFilters;
      setCurrentFilters({
        ...currentFilters,
        fromDate: dates ? dates[0] : undefined,
        toDate: dates ? dates[1] : undefined,
      });
      const { setPagination } = getCurrentTabData;
      setPagination((prev) => ({ ...prev, current: 1 }));
    },
    [getCurrentFilters, setCurrentFilters, getCurrentTabData],
  );

  const handleResetFilters = useCallback(() => {
    switch (activeTab) {
      case "today":
        setTodayFilters({
          patientName: "",
          clinicId: undefined,
          fromDate: dayjs().startOf("day"),
          toDate: dayjs().endOf("day"),
        });
        setTodayPagination((prev) => ({ ...prev, current: 1 }));
        break;
      case "tomorrow":
        setTomorrowFilters({
          patientName: "",
          clinicId: undefined,
          fromDate: dayjs().add(1, "day").startOf("day"),
          toDate: dayjs().add(1, "day").endOf("day"),
        });
        setTomorrowPagination((prev) => ({ ...prev, current: 1 }));
        break;
      case "yesterday":
        setYesterdayFilters({
          patientName: "",
          clinicId: undefined,
          fromDate: undefined,
          toDate: dayjs().subtract(1, "day").endOf("day"),
        });
        setYesterdayPagination((prev) => ({ ...prev, current: 1 }));
        break;
      case "all":
        setAllFilters({
          status: undefined,
          patientName: "",
          clinicId: undefined,
          fromDate: undefined,
          toDate: undefined,
        });
        setAllPagination((prev) => ({ ...prev, current: 1 }));
        break;
    }
    if (isMobile) {
      setFiltersDrawerVisible(false);
    }
  }, [activeTab, isMobile]); // In BookingsScreen - Enhanced patient navigation

  const handleInvoiceModalClose = useCallback(() => {
    setInvoiceModalVisible(false);
    setCurrentInvoiceBooking(null);
  }, []);
  const handleInvoiceClick = useCallback(
    async (booking) => {
      setCurrentInvoiceBooking(booking);
      setInvoiceModalVisible(true);
      if(role!=7){

        await fetchDoctorServices();
      }
    },
    [fetchDoctorServices],
  );

  const handlePatientInfoModalClose = useCallback(() => {
    setPatientInfoModalVisible(false);
    setCurrentActivateBooking(null);
    setCurrentPatientId(null)
    setUpdateOnly(false)

  }, []);
  const handleInvoiceSuccess = useCallback(() => {
    const { query } = getCurrentTabData;
    query.refetch();
    // message.success("Booking marked as active and invoice created!");
  }, [getCurrentTabData]);
  const handleMakeActive = useCallback((booking) => {
    setCurrentActivateBooking(booking);
    setPatientInfoModalVisible(true);
  }, []);

  const handleActiveClick = useCallback(
    async (booking) => {
      setCurrentInvoiceBooking(booking);
      setInvoiceModalVisible(true);
      await fetchDoctorServices();
    },
    [fetchDoctorServices],
  );
  const handleActivateBooking = useCallback(
    async (booking) => {
      try {
        await updateStatusMutation.mutateAsync({
          bookingId: booking.id,
          newStatus: "Active",
        });
        // message.success('Booking activated successfully!');
      } catch (error) {
        console.error("Error activating booking:", error);
        message.error("Failed to activate booking");
        throw error;
      }
    },
    [updateStatusMutation],
  );

  const handlePatientAssignment = useCallback(
    async (selectedPatient) => {
      try {
        const { query } = getCurrentTabData;
        await query.refetch();

        setPatientAssignmentModalVisible(false);
        setCurrentGuestBooking(null);

        message.success(
          `Guest booking successfully assigned to ${selectedPatient.fullName}`,
        );
      } catch (error) {
        console.error("Error handling patient assignment:", error);
      }
    },
    [getCurrentTabData],
  );
  const handleViewReportClick = useCallback((booking) => {
    setCurrentViewReportBooking(booking);
    setReportViewModalVisible(true);
  }, []);

  const handleReportViewModalClose = useCallback(() => {
    setReportViewModalVisible(false);
    setCurrentViewReportBooking(null);
  }, []);
  const FiltersContent = useMemo(() => {
    const currentFilters = getCurrentFilters;
    const isAllTab = activeTab === "all";

    return (
      <div className="">
        <div className="flex items-center gap-3 mb-4">
          <h3
            className={`font-semibold text-gray-800 flex items-center m-0 ${isMobile ? "text-sm" : "text-md"
              }`}
          >
            <FilterOutlined className="mr-2 text-sm" />
            Filters & Search
          </h3>
          <div className="w-44">

                   <Button
            icon={<ReloadOutlined />}
            onClick={handleResetFilters}
            size={isMobile ? "middle" : "small"}
            className="rounded-md w-20 bg-primary border-none text-white hover:from-gray-600 hover:to-gray-700 transform hover:scale-105 transition-all duration-300"
            block
          ></Button>
          </div>

        </div>

        <div
          className={`items-center   grid grid-cols-1 ${isMobile
            ? "gap-3"
            : isTablet
              ? "md:grid-cols-2 gap-4"
              : `lg:grid-cols-5  gap-2`
            }`}
        >
          <Input
            placeholder="🔍  patient name"
            prefix={<SearchOutlined className="text-gray-400" />}
            value={currentFilters.patientName}
            onChange={(e) => handleSearch(e.target.value)}
            allowClear
            size={isMobile ? "middle" : "large"}
            className="rounded-xl border-gray-200 hover:border-blue-400 focus:border-blue-500 transition-colors duration-300"
          />
{role!=7&&
          <Select
            placeholder="🏥by clinic"
            allowClear
            value={currentFilters.clinicId}
            onChange={handleClinicChange}
            loading={clinicsQuery.isLoading}
            size={isMobile ? "middle" : "large"}
            className="rounded-xl"
          >
            {clinicsQuery.data?.clinics?.map((clinic) => (
              <Option key={clinic.clinicId} value={clinic.clinicId}>
                {clinic.clinicName}
              </Option>
            ))}
          </Select>}
          <Select
            placeholder=" by Status"
            allowClear
            value={currentStatus}
            onChange={handleStatusChange}
            size={isMobile ? "middle" : "large"}
            className="rounded-xl"
          >
            {Object.keys(statusEnum).map((status) => (
              <Option key={status} value={status}>
                {status}
              </Option>
            ))}
          </Select>

          {isAllTab && (
            <>
              {/* <Select
                placeholder="📊by status"
                allowClear
                value={currentFilters.status}
                onChange={handleStatusChange}
                size={isMobile ? "middle" : "large"}
                className="rounded-xl"
              >
                {Object.keys(statusConfig).map((status) => (
                  <Option key={status} value={status}>
                    {status}
                  </Option>
                ))}
              </Select> */}
<DatePicker
  placeholder="From Date"
  format="MMM D, YYYY"
  value={currentFilters.fromDate ? dayjs(currentFilters.fromDate) : null}
  onChange={(date) => {
    const currentFilters = getCurrentFilters;
    setCurrentFilters({
      ...currentFilters,
      fromDate: date ? date.startOf("day") : undefined,
    });
    const { setPagination } = getCurrentTabData;
    setPagination((prev) => ({ ...prev, current: 1 }));
  }}
  suffixIcon={<CalendarOutlined />}
  size={isMobile ? "middle" : "large"}
  className="w-full rounded-xl border-gray-200 hover:border-blue-400"
/>

<DatePicker
  placeholder="To Date"
  format="MMM D, YYYY"
  value={currentFilters.toDate ? dayjs(currentFilters.toDate) : null}
  onChange={(date) => {
    const currentFilters = getCurrentFilters;
    setCurrentFilters({
      ...currentFilters,
      toDate: date ? date.endOf("day") : undefined,
    });
    const { setPagination } = getCurrentTabData;
    setPagination((prev) => ({ ...prev, current: 1 }));
  }}
  disabledDate={(current) =>
    currentFilters.fromDate
      ? current && current < dayjs(currentFilters.fromDate).startOf("day")
      : false
  }
  suffixIcon={<CalendarOutlined />}
  size={isMobile ? "middle" : "large"}
  className="w-full rounded-xl border-gray-200 hover:border-blue-400"
/>
         
      
        
            </>
            
          )}
         {!isAllTab && role !=7 &&( <div className="flex gap-2  items-center ">  <Button
            type=""
            icon={<BsCalendarPlus />}
            className="bg-secondary text-white flex items-center gap-2 justify-center "
            onClick={setCreateBookModal}
            size={isMobile ? "middle" : "middle"}
          >
            <span className="hidden sm:inline">Create Booking</span>
          </Button>
          <Button
            type=""
            icon={<PlusOutlined />}
            className="bg-primary text-white flex items-center gap-2 justify-center"
            onClick={setCreatePatientModalVisible}
            size={isMobile ? "middle" : "middle"}
          >
            <span className="hidden sm:inline">Add New Patient</span>
          </Button>

          </div>)}
        
          </div>
          {isAllTab && role !=7 && (
            
           <div className="flex gap-2 mt-2 items-center flex-1 w-full justify-end">  <Button
            type=""
            icon={<BsCalendarPlus />}
            className="bg-secondary text-white flex items-center gap-2 justify-center "
            onClick={setCreateBookModal}
            size={isMobile ? "middle" : "middle"}
          >
            <span className="hidden sm:inline">Create Booking</span>
          </Button>
          <Button
            type=""
            icon={<PlusOutlined />}
            className="bg-primary text-white flex items-center gap-2 justify-center"
            onClick={setCreatePatientModalVisible}
            size={isMobile ? "middle" : "middle"}
          >
            <span className="hidden sm:inline">Add New Patient</span>
          </Button>

          </div>)}
      </div>
    );
  }, [
    getCurrentFilters,
    activeTab,
    clinicsQuery.isLoading,
    clinicsQuery.data,
    handleSearch,
    handleClinicChange,
    handleStatusChange,
    handleDateChange,
    handleResetFilters,
    isMobile,
    isTablet,
  ]);

  // Memoized render functions
  const renderFilters = useMemo(() => {
    if (isMobile) {
      return (
        <>
          {/* Mobile Filter Button */}
          <div className="mb-4 flex justify-between items-center">
            <Button
              icon={<FilterOutlined />}
              onClick={() => setFiltersDrawerVisible(true)}
              className="rounded-xl bg-primary border-none text-white"
            >
              Filters
            </Button>
            {role!=7&&
            <Button
              type=""
              icon={<PlusOutlined />}
              title="Add Patient"
              className="bg-primary text-white flex items-center gap-2"
              onClick={setCreatePatientModalVisible}
            // onClick={() => document.querySelector('.custom-search input')?.focus()}
            >
              <span className="hidden sm:inline">Add Patient</span>
            </Button>}
          </div>

          {/* Mobile Filter Drawer */}
          <Drawer
            title="Filters & Search"
            placement="bottom"
            closable={true}
            onClose={() => setFiltersDrawerVisible(false)}
            open={filtersDrawerVisible}
            height="auto"
            className="mobile-filters-drawer"
          >
            {FiltersContent}
          </Drawer>
        </>
      );
    }

    return (
      <Card className="mb-6 sm:mb-8  shadow-sm border-0 bg-white/80 backdrop-blur-sm p-2 sm:p-0">
        {FiltersContent}
      </Card>
    );
  }, [isMobile, FiltersContent, filtersDrawerVisible]);

  const renderBookingsContent = useMemo(() => {
    const { query, pagination } = getCurrentTabData;
    const gridColsClass = `grid-cols-${getGridCols}`;
    const currentFilters = getCurrentFilters;

    return (
      <Spin spinning={query.isLoading || clinicsQuery.isLoading}>
        {query.data?.bookings?.length > 0 ? (
          <>
            <div
              className={`grid grid-cols-1 ${isMobile
                ? "gap-4"
                : isTablet
                  ? "sm:grid-cols-2 gap-4 sm:gap-6"
                  : "sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6"
                } mb-6 sm:mb-8`}
            >
              {query.data.bookings.map((booking, index) => (
                <BookingCard
                  key={booking.id}
                  booking={booking}
                  index={index}
                  onGuestAssignment={handleGuestAssignmentClick}
                  onComplete={handleCompleteClick}
                  onCancel={handleCancelClick}
                  onInvoiceClick={handleInvoiceClick}
                  onMakeActive={handleMakeActive}
                  updateStatusMutation={updateStatusMutation}
                  isMobile={isMobile}
                  onAssignToSpecialist={handleAssignToSpecialistClick}
                  onViewReport={handleViewReportClick}
                  onAssignToConsultant={handleAssignToConsultantClick}
                  setAssignConsultantBooking={setAssignConsultantBooking}
                  setAssignConsultantVisible={setAssignConsultantVisible}
                  setPatientInfoModalVisible={setPatientInfoModalVisible}
                  setCurrentPatientId={setCurrentPatientId}
                  setUpdateOnly={setUpdateOnly}


                />
              ))}
            </div>

            <div className="flex justify-center">
              <div
                className={`bg-white rounded-xl sm:rounded-2xl shadow-lg ${isMobile ? "p-2" : "p-4"
                  }`}
              >
                <Pagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={query.data?.totalCount || 0}
                  onChange={(page, pageSize) => {
                    const { setPagination } = getCurrentTabData;
                    setPagination({
                      ...pagination,
                      current: page,
                      pageSize,
                    });
                  }}
                  showTotal={(total) => (
                    <span
                      className={`text-gray-600 font-medium ${isMobile ? "text-xs" : ""
                        }`}
                    >
                      {isMobile ? `${total} total` : `Total ${total} bookings`}
                    </span>
                  )}
                  size={isMobile ? "small" : "default"}
                  showSizeChanger={!isMobile}
                  showQuickJumper={!isMobile}
                  className="modern-pagination"
                />
              </div>
            </div>
          </>
        ) : (
          !query.isLoading && (
            <div className="text-center py-12 sm:py-16">
              {activeTab=="today" || activeTab=="tomorrow" || activeTab=="yesterday" ? (
              <div className={`${isMobile ? "text-lg" : "text-xl"} mb-4 flex items-center justify-center`}>
                {currentFilters?.fromDate 
                  ? <div className="flex items-center gap-2 text-3xl text-gray-600"><BiCalendarEvent className=""/> {dayjs(currentFilters.fromDate).format(" D MMM")}  </div>
                  : <BiCalendarEvent className="text-3xl text-gray-600"/>}
              </div>): <div className={`${isMobile ? "text-lg" : "text-xl"} mb-4`}>
                {currentFilters?.fromDate && currentFilters?.toDate
                  ? `${dayjs(currentFilters.fromDate).format("MMM D")} - ${dayjs(  currentFilters.toDate).format("MMM D, YYYY")}`
                  : null}
              </div>}
              <h3
                className={`font-bold text-gray-600 mb-2 ${isMobile ? "text-lg" : "text-2xl"
                  }`}
              >
                No Bookings Found
              </h3>
              <p className={`text-gray-500 ${isMobile ? "text-sm" : ""}`}>
                Try adjusting your filters or check back later.
              </p>
            </div>
          )
        )}
      </Spin>
    );
  }, [
    getCurrentTabData,
    getGridCols,
    clinicsQuery.isLoading,
    handleCompleteClick,
    handleCancelClick,
    updateStatusMutation,
    isMobile,
    isTablet,
  ]);

  // Memoized tab title function
  const getTabTitle = useCallback(
    (key, count) => {
      const titles = {
        today: isMobile ? `Today (${count || 0})` : `📅 Today (${count || 0})`,
        tomorrow: isMobile
          ? `Tomorrow (${count || 0})`
          : `📅 Coming Tomorrow (${count || 0})`,
        yesterday: isMobile
          ? `Previous (${count || 0})`
          : `📅 Previous (${count || 0})`,
        all: isMobile
          ? `All (${count || 0})`
          : `📋 All Bookings (${count || 0})`,
      };
      return titles[key];
    },
    [isMobile],
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-2">
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes shimmer {
          0% {
            background-position: -1000px 0;
          }
          100% {
            background-position: 1000px 0;
          }
        }

        .shimmer {
          background: linear-gradient(
            90deg,
            #f0f0f0 25%,
            #e0e0e0 50%,
            #f0f0f0 75%
          );
          background-size: 1000px 100%;
          animation: shimmer 2s infinite;
        }

        .ant-tabs-tab {
          border-width: 1px;
          border-style: solid;
          border-color: #298e9f !important;
          margin-right: ${isMobile ? "4px" : "8px"} !important;
          padding: ${isMobile ? "4px 8px" : "8px 8px"} !important;
          font-size: ${isMobile ? "12px" : "14px"} !important;
        }
        .ant-tabs-tab-active {
          border-bottom: ${isMobile ? "6px" : "9px"} !important;
          color: white !important;
        }

        .mobile-filters-drawer .ant-drawer-body {
          padding: 16px !important;
        }

        .modern-pagination .ant-pagination-item {
          margin: ${isMobile ? "0 2px" : "0 4px"} !important;
        }

        @media (max-width: 640px) {
          .ant-tabs-content-holder {
            padding: 0 !important;
          }

          .ant-card-body {
            padding: 12px !important;
          }
        }

        @media (max-width: 768px) {
          .ant-modal {
            margin: 0 !important;
            max-width: none !important;
          }
        }
      `}</style>

      <div>
        {/* Responsive Tabs */}
        <Card
          className={`mb-1 sm:mb-2 shadow-sm border-0 bg-white/80 backdrop-blur-sm ${isMobile ? "px-2" : ""
            }`}
        >
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            type="line"
            size={isMobile ? "small" : "large"}
            className="custom-tabs"
            tabBarGutter={isMobile ? 4 : 8}
          >
            <TabPane
              tab={getTabTitle("today", todayBookingsQuery.data?.totalCount)}
              key="today"
            />
            <TabPane
              tab={getTabTitle(
                "tomorrow",
                tomorrowBookingsQuery.data?.totalCount,
              )}
              key="tomorrow"
            />
            <TabPane
              tab={getTabTitle(
                "yesterday",
                yesterdayBookingsQuery.data?.totalCount,
              )}
              key="yesterday"
            />
            <TabPane
              tab={getTabTitle("all", allBookingsQuery.data?.totalCount)}
              key="all"
            />
          </Tabs>
        </Card>

        {renderFilters}

        {renderBookingsContent}
      </div>
      <ReportViewModal
        visible={reportViewModalVisible}
        onCancel={handleReportViewModalClose}
        bookingId={currentViewReportBooking?.id}
        userId={currentViewReportBooking?.profileId}
        booking={currentViewReportBooking}
        isMobile={isMobile}
        onEditInvoice={handleEditInvoiceFromReport}
        onAssignSpecialist={handleAssignSpecialistFromReport}
        showActions={true}
      />

      <InvoiceModal
        visible={invoiceModalVisible}
        onCancel={handleInvoiceModalClose}
        booking={currentInvoiceBooking}
        services={doctorServices}
        servicesLoading={servicesLoading}
        isMobile={isMobile}
        onSuccess={handleInvoiceSuccess}
      />
      <PatientAssignmentModal
        visible={patientAssignmentModalVisible}
        onCancel={handlePatientAssignmentModalClose}
        onAssign={handlePatientAssignment}
        booking={currentGuestBooking}
        isMobile={isMobile}

      />
      <SpecialistAssignmentModal
        visible={specialistAssignmentModalVisible}
        onCancel={handleSpecialistModalClose}
        onAssign={handleSpecialistAssignment}
        booking={currentAssignmentBooking}
        isMobile={isMobile}
        handleCompleteClick={handleCompleteClick}

      />
      {/* Enhanced Responsive Cancel Booking Modal */}
      <Modal
        title={
          <div className="flex items-center">
            <div
              className={`${isMobile ? "w-6 h-6" : "w-8 h-8"
                } bg-gradient-to-r from-red-400 to-red-600 rounded-full flex items-center justify-center ${isMobile ? "mr-2" : "mr-3"
                }`}
            >
              <TbCancel
                className={`text-white ${isMobile ? "text-xs" : "text-sm"}`}
              />
            </div>
            <span className={isMobile ? "text-sm" : ""}>Cancel Booking</span>
          </div>
        }
        open={cancelModalVisible}
        onOk={handleCancelConfirm}
        onCancel={handleCancelModalClose}
        okText=" Confirm Cancellation"
        cancelText="Close"
        confirmLoading={updateStatusMutation.isLoading}
        width={isMobile ? "95vw" : 500}
        className="modern-modal"
        centered={isMobile}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="reason"
            label={
              <span className={isMobile ? "text-sm" : ""}>
                Cancellation Reason
              </span>
            }
            rules={[
              {
                required: true,
                message: "Please provide a cancellation reason",
              },
            ]}
          >
            <TextArea
              rows={isMobile ? 3 : 4}
              placeholder="Enter the reason for cancellation..."
              className="rounded-xl"
              size={isMobile ? "middle" : "large"}
            />
          </Form.Item>
        </Form>
      </Modal>
      <ConsultantAssignmentModal
        visible={consultantAssignModalVisible}
        onCancel={handleConsultantAssignModalClose}
        onAssign={handleConsultantAssigned}
        session={{
          userId: currentConsultantBooking?.patientId,
          patientId: currentConsultantBooking?.profileId,
          patientName: currentConsultantBooking?.patientName,
        }}
        isMobile={isMobile}
      />
      <AssignBookingToConsultantModal
        visible={assignConsultantVisible}
        onCancel={() => {
          setAssignConsultantVisible(false);
          setAssignConsultantBooking(null);
        }}
        onAssign={(consultant) => {
          const { query } = getCurrentTabData;

          query.refetch();
          setAssignConsultantVisible(false);
        }}
        booking={assignConsultantBooking}
        isMobile={isMobile}
      />
      <ReportModal
        isPrivateReport={isPrivate}
        visible={reportModalVisible}
        onCancel={() => setReportModalVisible(false)}
        bookingId={currentReportBooking?.id}
        reportId={currentReportBooking?.reportId}
        setCurrentReportId={(id) => {
          setCurrentReportBooking((prev) => ({ ...prev, reportId: id }));
        }}
        setCurrentReportBooking={setCurrentReportBooking}
        onSuccess={() => {
          queryClient.invalidateQueries({
            queryKey: ["bookings"],
            refetchType: "active",
          });
        }}
        isMobile={isMobile}
      />
      <PatientInfoModal
        visible={patientInfoModalVisible}
        onCancel={handlePatientInfoModalClose}
        booking={currentActivateBooking}
        onActivate={handleActivateBooking}
        isMobile={isMobile}
        patientId={currentPatientId}
        updateOnly={updateOnly}
        onUpdate={() => {
          const { query } = getCurrentTabData;
          query.refetch();
          setCurrentPatientId(null)
        }}
      />
      <CreatePatientModal
        visible={createPatientModalVisible}
        onClose={() => { setCreatePatientModalVisible(false); setCurrentPatientId(null) }}
        isMobile={isMobile}
      />
      <CreateBookingModal
        visible={createBookModal}
        onCancel={() => { setCreateBookModal(false); setCurrentPatientId(null) }}
        onSuccess={() => {
          const { query } = getCurrentTabData;
          query.refetch();
          message.success("Booking created successfully!");
        }}
        isMobile={isMobile}
      />
    </div>
  );
};

BookingCard.displayName = "BookingCard";

export default BookingsScreen;
